import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Trophy, CalendarDays, Target, RefreshCw,
  Download, CheckCircle2, Clock, Play, GitBranch,
  Edit2, Share2, Copy, MapPin, Calendar, Users, Zap,
  AlertTriangle, Check, CalendarClock, ChevronDown, ChevronRight, Search, Plus, Trash2,
  Award, Star, Upload, Settings, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import api, { BACKEND_URL } from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';
import { SportFormatsTab, GroupsTab, EncountersTab, GroupStandingsTab } from './TournamentManagement';
import { DatePickerField, TimePickerField, StatusBadge, formatRound, TeamFormRowEdit, SeedAssignPanel } from './TournamentHelpers';
import { BracketView } from './TournamentBracket';
import { RegistrationTab } from './TournamentRegistrationTab';
import { TournamentDrawDialog } from './TournamentDrawDialog';

export default function TournamentDetailPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';

  const [tournament, setTournament] = useState(null);
  const [fixtures, setFixtures]     = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [standings, setStandings]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgTeams, setOrgTeams]     = useState([]);
  const [tournSponsors, setTournSponsors] = useState([]);  // tournament-level sponsors
  const [orgSponsors, setOrgSponsors]     = useState([]);  // org sponsors for picker
  const [sponsorTypes, setSponsorTypes]   = useState([]);  // sponsor type options
  const [loading, setLoading]       = useState(true);
  const [addSponsorOpen, setAddSponsorOpen] = useState(false);
  const [addingSponsor, setAddingSponsor]   = useState(false);
  const [sponsorForm, setSponsorForm] = useState({ name: '', type_id: '', tagline: '', website_url: '' });

  // ── Team Setup for existing tournament ──────────────────────────
  const [teamSetupOpen, setTeamSetupOpen] = useState(false);
  const [setupNumTeams, setSetupNumTeams] = useState('4');
  const [setupTeamForms, setSetupTeamForms] = useState([]);
  const [settingUpTeams, setSettingUpTeams] = useState(false);
  // ── Draw dialog state (single boolean — dialog manages its own draw state) ──
  const [activeTab, setActiveTab] = useState(admin ? 'fixtures' : 'gstandings');
  const [drawOpen, setDrawOpen] = useState(false);
  // ── Assign existing org teams to this tournament ─────────────

  // ── Tournament Admin Management ──────────────────────────────
  const [tournAdmins, setTournAdmins] = useState([]);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [addAdminPhone, setAddAdminPhone] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  // Fixture mode selection
  const [fixtureModeOpen, setFixtureModeOpen] = useState(false);
  const [fixtureMode, setFixtureMode] = useState('within_group');
  const [generatingFixtures, setGeneratingFixtures] = useState(false);

  useEffect(() => {
    if (tournamentId) {
      const token = localStorage.getItem('btccp_access_token');
      fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/admins`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : []).then(setTournAdmins).catch(()=>{});
    }
  }, [tournamentId, orgSlug]); // eslint-disable-line

  const handleAddAdmin = async () => {
    if (!addAdminPhone.trim()) return;
    setAddingAdmin(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/admins`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: addAdminPhone.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.admin?.name || addAdminPhone} added as tournament admin`);
        setAddAdminPhone(''); setAddAdminOpen(false);
        const r2 = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/admins`, { headers: { Authorization: `Bearer ${localStorage.getItem('btccp_access_token')}` } });
        if (r2.ok) setTournAdmins(await r2.json());
      } else { toast.error(data.detail || 'Failed'); }
    } finally { setAddingAdmin(false); }
  };

  const handleRemoveAdmin = async (userId, name) => {
    if (!window.confirm(`Remove ${name} as tournament admin?`)) return;
    await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/admins/${userId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('btccp_access_token')}` } });
    toast.success('Admin removed');
    setTournAdmins(prev => prev.filter(a => a.user_id !== userId));
  };


  const [assignTeamsOpen, setAssignTeamsOpen] = useState(false);
  const [assignSelected, setAssignSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  // ── Teams & Players tab ───────────────────────────
  const [playerCategories, setPlayerCategories] = useState([]);
  const [tournamentPlayers, setTournamentPlayers] = useState([]); // all org players for picker
  // Tournament-level active player categories
  const [allCategories, setAllCategories] = useState([]);
  const [savingCats, setSavingCats] = useState(false);
  const [teamRosters, setTeamRosters] = useState({}); // teamId → player[]
  const [addPlayerTarget, setAddPlayerTarget] = useState(null); // {teamId, teamName}
  const [addPlayerForm, setAddPlayerForm] = useState({ player_id:'', category:'', sports:[] });
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [editingRoster, setEditingRoster] = useState(null); // {teamId, playerId}
  const [editCat, setEditCat] = useState('');
  const [savingRosterCat, setSavingRosterCat] = useState(false);
  // ── Assign teams to groups (all groups at once) ───
  const [groupAssignOpen, setGroupAssignOpen] = useState(false);
  const [groupsData, setGroupsData] = useState([]); // all groups for this tournament
  const [groupSlots, setGroupSlots] = useState({}); // groupId → [teamId, ...]
  const [savingGroupAssign, setSavingGroupAssign] = useState(false);

  const openAssignTeams = () => {
    // Pre-select teams already assigned to this tournament
    const already = new Set(orgTeams.filter(t => t.tournament_id === tournamentId).map(t => t.id));
    setAssignSelected(already);
    setAssignTeamsOpen(true);
  };

  const handleAssignTeams = async () => {
    setAssigning(true);
    const token = localStorage.getItem('btccp_access_token');
    let ok = 0;
    for (const t of orgTeams) {
      const shouldBe = assignSelected.has(t.id);
      const isCurrent = t.tournament_id === tournamentId;
      if (shouldBe && !isCurrent) {
        const res = await fetch(`/api/orgs/${orgSlug}/teams/${t.id}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ tournament_id: tournamentId }),
        });
        if (res.ok) ok++;
      }
    }
    toast.success(`${ok} team${ok !== 1 ? 's' : ''} assigned to this tournament`);
    setAssignTeamsOpen(false);
    setAssigning(false);
    fetchAll();
  };

  // ── Fetch rosters for all tournament teams ────────
  const fetchTeamRosters = async (teamsToFetch) => {
    const token = localStorage.getItem('btccp_access_token');
    const results = {};
    await Promise.allSettled((teamsToFetch||[]).map(async t => {
      const r = await fetch(`/api/orgs/${orgSlug}/teams/${t.id}/roster`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) results[t.id] = await r.json();
    }));
    setTeamRosters(results);
  };
  const fetchPlayerCategories = async () => {
    const token = localStorage.getItem('btccp_access_token');
    const [rCat, rPl] = await Promise.allSettled([
      fetch(`/api/orgs/${orgSlug}/player-categories`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`/api/orgs/${orgSlug}/players?limit=999`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (rCat.status === 'fulfilled' && rCat.value.ok) { const d = await rCat.value.json(); setPlayerCategories(Array.isArray(d) ? d : []); setAllCategories(Array.isArray(d) ? d : []); }
    if (rPl.status === 'fulfilled' && rPl.value.ok)  { const d = await rPl.value.json();  setTournamentPlayers(Array.isArray(d) ? d : d.items||[]); }
  };
  // Fetch all categories for the Format tab on mount
  useEffect(() => {
    const token = localStorage.getItem('btccp_access_token');
    fetch(`/api/orgs/${orgSlug}/player-categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(d => setAllCategories(Array.isArray(d) ? d : [])).catch(()=>{});
  }, [orgSlug]); // eslint-disable-line

  const saveTournamentCategories = async (codes) => {
    setSavingCats(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_categories: codes }),
      });
      if (res.ok) { toast.success('Player categories saved'); fetchAll(); }
      else toast.error('Failed to save');
    } finally { setSavingCats(false); }
  };
  const handleAddPlayer = async () => {
    if (!addPlayerTarget || !addPlayerForm.player_id) return;
    setSavingPlayer(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${addPlayerTarget.teamId}/roster`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: addPlayerForm.player_id, category: addPlayerForm.category }),
      });
      if (res.ok) {
        toast.success('Player added to team');
        setAddPlayerTarget(null);
        setAddPlayerForm({ player_id:'', category:'' });
        const tournTeams = orgTeams.filter(t => t.tournament_id === tournamentId);
        fetchTeamRosters(tournTeams);
      } else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } finally { setSavingPlayer(false); }
  };
  const saveRosterCategory = async () => {
    if (!editingRoster) return;
    setSavingRosterCat(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/teams/${editingRoster.teamId}/roster/${editingRoster.playerId}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: editCat }),
      });
      if (res.ok) {
        toast.success('Category updated');
        setEditingRoster(null);
        const tournTeams = orgTeams.filter(t => t.tournament_id === tournamentId);
        fetchTeamRosters(tournTeams);
      } else { toast.error('Failed to update category'); }
    } finally { setSavingRosterCat(false); }
  };
  const openGroupAssign = async () => {
    const token = localStorage.getItem('btccp_access_token');
    const r = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/groups`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const grps = (await r.json()).filter(g => g.letter !== 'KO');
      setGroupsData(grps);
      const slots = {};
      grps.forEach(g => { slots[g.id] = [...(g.team_ids||[])]; });
      setGroupSlots(slots);
    }
    setGroupAssignOpen(true);
  };
  const saveGroupAssign = async () => {
    setSavingGroupAssign(true);
    const token = localStorage.getItem('btccp_access_token');
    let ok = 0;
    for (const grp of groupsData) {
      const res = await fetch(`/api/orgs/${orgSlug}/groups/${grp.id}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_ids: (groupSlots[grp.id]||[]).filter(Boolean) }),
      });
      if (res.ok) ok++;
    }
    toast.success(`${ok} group${ok!==1?'s':''} updated`);
    setGroupAssignOpen(false);
    setSavingGroupAssign(false);
    fetchAll();
  };


  const emptyTeamSetup = (i) => ({ name:'', short_name:'', color: TEAM_COLORS_SETUP[i % TEAM_COLORS_SETUP.length], logo_b64:null, logo_preview:null, sponsor:'' });

  const openTeamSetup = () => {
    const n = parseInt(setupNumTeams) || 4;
    setSetupTeamForms(Array.from({ length: n }, (_, i) => emptyTeamSetup(i)));
    setTeamSetupOpen(true);
  };

  const handleTeamSetupCreate = async () => {
    const valid = setupTeamForms.filter(t => t.name.trim());
    if (valid.length < 2) { toast.error('Please name at least 2 teams'); return; }
    setSettingUpTeams(true);
    try {
      const createdIds = [];
      for (const tf of valid) {
        const teamRes = await api.post(`/orgs/${orgSlug}/teams`, {
          name: tf.name,
          short_name: tf.short_name || tf.name.slice(0,4).toUpperCase(),
          color: tf.color || null,
          tournament_id: tournamentId,
        });
        const teamId = teamRes.data.id;
        if (tf.logo_b64) await api.patch(`/orgs/${orgSlug}/teams/${teamId}`, { logo_b64: tf.logo_b64 }).catch(() => {});
        createdIds.push({ teamId, sponsor: tf.sponsor });
      }
      // Generate fixtures
      if (createdIds.length >= 2) {
        await api.post(`/orgs/${orgSlug}/tournaments/${tournamentId}/generate-fixtures`, {
          entries: createdIds.map(({ teamId }) => ({ type:'team', id:teamId })),
          seeding: 'linear',
        }).catch(() => {});
      }
      // Save team sponsors
      for (const { teamId, sponsor } of createdIds) {
        if (sponsor.trim() && sponsorTypes.length > 0) {
          try {
            const spRes = await api.post(`/orgs/${orgSlug}/sponsors`, { name: sponsor.trim(), type_id: sponsorTypes.find(t=>t.code==='team')?.id || sponsorTypes[0]?.id });
            await api.post(`/orgs/${orgSlug}/tournaments/${tournamentId}/sponsors`, { sponsor_id: spRes.data.id, type_code:'team' });
          } catch (_) {}
        }
      }
      toast.success(`${createdIds.length} teams created and fixtures generated!`);
      setTeamSetupOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set up teams');
    } finally {
      setSettingUpTeams(false);
    }
  };

  const [editOpen, setEditOpen]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [regOpen, setRegOpen]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedCatId, setSelectedCatId] = useState('');
  const [regTeams, setRegTeams]     = useState({});
  const [regSeeding, setRegSeeding] = useState('linear');
  const [editForm, setEditForm]     = useState({
    name: '', start_date: '', end_date: '', venue_name: '', banner_url: '',
  });
  const [bannerB64, setBannerB64]       = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const bannerFileRef = useRef(null);

  // ── Fixture scheduling (localStorage-based) ──────────────────
  const SCHED_KEY = `btccp_schedule_${tournamentId}`;
  const [schedule, setSchedule]         = useState(() => {
    try { return JSON.parse(localStorage.getItem(SCHED_KEY) || '{}'); } catch { return {}; }
  });
  const [schedOpen, setSchedOpen]       = useState(false);
  const [schedTarget, setSchedTarget]   = useState(null); // fixture object
  const [schedForm, setSchedForm]       = useState({ date: '', time: '', court: '' });

  const saveSchedule = (next) => {
    setSchedule(next);
    localStorage.setItem(SCHED_KEY, JSON.stringify(next));
  };

  const openScheduler = (fx) => {
    setSchedTarget(fx);
    const existing = schedule[fx.id] || {};
    setSchedForm({
      date:  existing.date    || '',
      time:  existing.rawTime || '',   // use raw time for re-edit
      court: existing.court   || '',
    });
    setSchedOpen(true);
  };

  const handleSaveSchedule = () => {
    if (!schedTarget) return;
    // Convert HH:MM (24h) to human-readable display
    const displayTime = schedForm.time
      ? (() => {
          const [h, m] = schedForm.time.split(':').map(Number);
          const ampm = h >= 12 ? 'PM' : 'AM';
          const h12  = h === 0 ? 12 : h > 12 ? h - 12 : h;
          return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
        })()
      : '';
    const updated = {
      ...schedule,
      [schedTarget.id]: {
        date:        schedForm.date,
        time:        displayTime,
        rawTime:     schedForm.time,   // keep raw for re-edit
        court:       schedForm.court,
      },
    };
    saveSchedule(updated);
    setSchedOpen(false);
    toast.success('Schedule saved locally');
  };

  const clearSchedule = (fxId) => {
    const updated = { ...schedule };
    delete updated[fxId];
    saveSchedule(updated);
  };

  useEffect(() => { fetchAll(); }, [tournamentId, activeOrgSlug]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, fRes, sRes, cRes, teamsRes, sponsorsRes, typesRes, orgSpRes, encRes] = await Promise.allSettled([
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}`),
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/fixtures`),
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/standings`),
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/categories`),
        api.get(`/orgs/${orgSlug}/teams`),
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/sponsors`),
        api.get(`/orgs/${orgSlug}/sponsor-types`),
        api.get(`/orgs/${orgSlug}/sponsors`),
        api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/encounters`),
      ]);
      if (encRes.status === 'fulfilled') { const d = encRes.value.data; setEncounters(Array.isArray(d) ? d : []); }
      if (tRes.status === 'fulfilled') {
        const t = tRes.value.data;
        setTournament(t);
        setEditForm({ name: t.name || '', start_date: t.start_date ? t.start_date.slice(0, 10) : '', end_date: t.end_date ? t.end_date.slice(0, 10) : '', venue_name: t.venue_name || '', banner_url: t.banner_url || '' });
        if (t.banner_url) setBannerPreview(t.banner_url);
      }
      if (fRes.status === 'fulfilled') { const d = fRes.value.data; setFixtures(Array.isArray(d) ? d : d.items || []); }
      if (sRes.status === 'fulfilled') { const d = sRes.value.data; setStandings(Array.isArray(d) ? d : d.items || []); }
      if (cRes.status === 'fulfilled') {
        const cats = Array.isArray(cRes.value.data) ? cRes.value.data : [];
        setCategories(cats);
        if (cats.length > 0 && !selectedCatId) setSelectedCatId(cats[0].id);
      }
      if (teamsRes.status === 'fulfilled') setOrgTeams(Array.isArray(teamsRes.value.data) ? teamsRes.value.data : []);
      if (sponsorsRes.status === 'fulfilled') { const d = sponsorsRes.value.data; setTournSponsors(Array.isArray(d) ? d : []); }
      if (typesRes.status === 'fulfilled') { const d = typesRes.value.data; setSponsorTypes(Array.isArray(d) ? d : []); }
      if (orgSpRes.status === 'fulfilled') { const d = orgSpRes.value.data; setOrgSponsors(Array.isArray(d) ? d : []); }
    } catch {
      toast.error('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTournament = async () => {
    setSaving(true);
    try {
      await api.patch(`/orgs/${orgSlug}/tournaments/${tournamentId}`, {
        name:       editForm.name || undefined,
        start_date: editForm.start_date || null,
        end_date:   editForm.end_date   || null,
        venue_name: editForm.venue_name || null,
        banner_url: bannerB64 || editForm.banner_url || null,
      });
      toast.success('Tournament updated');
      if (bannerB64) { setBannerPreview(bannerB64); setBannerB64(null); }
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Banner must be under 3MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setBannerB64(ev.target.result); setBannerPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/public/tournaments/${tournamentId}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Scoreboard link copied!'))
      .catch(() => toast.info(`Share: ${url}`));
  };

  // ── Sponsor management ─────────────────────────────────────────
  const handleAddSponsor = async () => {
    if (!sponsorForm.name.trim()) { toast.error('Sponsor name is required'); return; }
    setAddingSponsor(true);

    const token = localStorage.getItem('btccp_access_token');

    // Refresh token first
    try {
      const rt = localStorage.getItem('btccp_refresh_token');
      if (rt) {
        const rRes = await fetch(`/api/auth/refresh`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (rRes.ok) {
          const rData = await rRes.json();
          const nt = rData?.tokens?.access_token || rData?.access_token;
          if (nt) localStorage.setItem('btccp_access_token', nt);
        }
      }
    } catch (_) {}

    const freshToken = localStorage.getItem('btccp_access_token');
    const authHeader = { 'Authorization': `Bearer ${freshToken}`, 'Content-Type': 'application/json' };

    try {
      // Derive org slug from fresh /auth/me
      let slug = orgSlug;
      try {
        const meRes = await fetch(`/api/auth/me`, { headers: authHeader });
        if (meRes.ok) {
          const me = await meRes.json();
          const fs = me?.memberships?.[0]?.slug;
          if (fs) slug = fs;
        }
      } catch (_) {}

      // If sponsor types not loaded, try to fetch them
      let types = sponsorTypes;
      if (!types.length) {
        try {
          const stRes = await fetch(`/api/orgs/${slug}/sponsor-types`, { headers: authHeader });
          if (stRes.ok) {
            const stData = await stRes.json();
            if (Array.isArray(stData) && stData.length) {
              setSponsorTypes(stData);
              types = stData;
            }
          }
        } catch (_) {}
      }

      const typeId = sponsorForm.type_id || types[0]?.id;
      const typeCode = types.find(t => t.id === typeId)?.code || 'team';

      // 1. Create org-level sponsor via raw fetch
      const spRes = await fetch(`/api/orgs/${slug}/sponsors`, {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          name:        sponsorForm.name.trim(),
          type_id:     typeId || undefined,
          tagline:     sponsorForm.tagline || undefined,
          website_url: sponsorForm.website_url || undefined,
        }),
      });
      const spData = await spRes.json();
      if (!spRes.ok) {
        toast.error(spData?.detail || `Failed to create sponsor (${spRes.status})`);
        return;
      }

      // 2. Link to tournament
      const linkRes = await fetch(`/api/orgs/${slug}/tournaments/${tournamentId}/sponsors`, {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ sponsor_id: spData.id, type_code: typeCode, display_order: tournSponsors.length + 1 }),
      });
      const linkData = await linkRes.json();
      if (!linkRes.ok) {
        toast.error(linkData?.detail || `Failed to link sponsor (${linkRes.status})`);
        return;
      }

      toast.success(`${sponsorForm.name} added as sponsor!`);
      setAddSponsorOpen(false);
      setSponsorForm({ name: '', type_id: '', tagline: '', website_url: '' });
      fetchAll();
    } catch (err) {
      toast.error(`Failed to add sponsor: ${err.message}`);
    } finally {
      setAddingSponsor(false);
    }
  };

  // Derive registered team IDs from existing fixtures
  const registeredTeamIds = new Set(
    fixtures.flatMap(fx => [
      fx.home?.id, fx.away?.id,
      fx.team1?.id, fx.team2?.id,
    ].filter(Boolean))
  );

  const openRegistration = () => {
    // Pre-select currently registered teams
    const initial = {};
    orgTeams.forEach(t => {
      initial[t.id] = { selected: registeredTeamIds.has(t.id), seed: '' };
    });
    setRegTeams(initial);
    if (categories.length > 0) setSelectedCatId(categories[0].id);
    setRegOpen(true);
  };

  const toggleRegTeam = (id) => {
    setRegTeams(prev => ({
      ...prev,
      [id]: { ...prev[id], selected: !prev[id]?.selected, seed: prev[id]?.seed || '' },
    }));
  };

  const setRegTeamSeed = (id, seed) => {
    setRegTeams(prev => ({
      ...prev,
      [id]: { ...prev[id], seed },
    }));
  };

  const handleGenerateFixtures = async () => {
    const entries = orgTeams
      .filter(t => regTeams[t.id]?.selected)
      .map(t => ({
        type: 'team',
        id: t.id,
        ...(regTeams[t.id]?.seed ? { seed: parseInt(regTeams[t.id].seed, 10) } : {}),
      }));

    if (entries.length < 2) { toast.error('Select at least 2 teams'); return; }

    setGenerating(true);
    try {
      await api.post(`/orgs/${orgSlug}/tournaments/${tournamentId}/generate-fixtures`, {
        entries,
        seeding: regSeeding,
        ...(selectedCatId ? { category_id: selectedCatId } : {}),
      });
      toast.success(`Fixtures generated for ${entries.length} teams`);
      setRegOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate fixtures');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/orgs/${orgSlug}/tournaments/${tournamentId}/export/fixtures.csv`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href = url; a.download = `${tournament?.name || 'fixtures'}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  // Group fixtures by round
  const byRound = fixtures.reduce((acc, fx) => {
    const r = fx.round || 'unknown';
    if (!acc[r]) acc[r] = [];
    acc[r].push(fx);
    return acc;
  }, {});

  const completedCount = fixtures.filter(f => ['completed','finished'].includes(f.status?.toLowerCase())).length;
  const liveCount      = fixtures.filter(f => f.status?.toLowerCase() === 'live').length;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-8 w-48 bg-[#1d2230] mb-6" />
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 bg-[#1d2230] rounded-xl" />)}
        </div>
        <Skeleton className="h-64 bg-[#1d2230] rounded-xl" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#8a93a3]">Tournament not found</p>
        <Button onClick={() => navigate('/admin/tournaments')} className="mt-4">Back</Button>
      </div>
    );
  }

  // Compute available sports for SportFormatsTab
  const availableSports = (() => {
    const hardcoded = [
      {code:'table_tennis',name:'Table Tennis'},{code:'badminton',name:'Badminton'},
      {code:'carrom',name:'Carrom'},{code:'pickleball',name:'Pickleball'},
      {code:'chess',name:'Chess'},{code:'squash',name:'Squash'},
      {code:'cricket',name:'Cricket'},{code:'football',name:'Football'},
    ];
    const fromTournament = (tournament.sport_codes || []).map(sc => ({
      code: sc,
      name: sc.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    }));
    const all = [...fromTournament, ...hardcoded];
    return all.filter((v, i, a) => a.findIndex(x => x.code === v.code) === i);
  })();

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="tournament-detail-page">
      {/* Banner hero image */}
      {tournament.banner_url && (
        <div className="relative w-full h-36 sm:h-48 rounded-2xl overflow-hidden mb-6 border border-[#252a37]"
          data-testid="tournament-banner">
          <img src={tournament.banner_url} alt={tournament.name}
            className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#06080d]/80 via-transparent to-transparent"/>
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">{tournament.name}</h1>
            {admin && (
            <button onClick={() => setEditOpen(true)}
              className="text-xs text-white/70 hover:text-white flex items-center gap-1 bg-black/30 px-2 py-1 rounded-lg backdrop-blur-sm">
              <Upload className="h-3 w-3"/>Change banner
            </button>
            )}
          </div>
        </div>
      )}

      {/* Back + header */}
      <div className={`flex items-start justify-between gap-4 flex-wrap ${tournament.banner_url ? 'mb-4' : 'mb-6'}`}>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/tournaments')}
            className="text-[#8a93a3] hover:text-[#f0f2f7]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            {!tournament.banner_url && (
              <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7]">{tournament.name}</h1>
            )}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs capitalize">
                {tournament.format?.replace(/_/g, ' ') || 'Round Robin'}
              </Badge>
              {tournament.sport_codes?.map(s => (
                <Badge key={s} className="bg-[rgba(245,197,24,0.1)] text-[#f5c518] border border-[rgba(245,197,24,0.25)] text-xs capitalize">
                  {s.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {admin && (
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            size="sm"
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="tournament-edit-button"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </Button>
          )}
          {/* Broadcast TV link */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${window.location.origin}/tv/${tournamentId}`;
              window.open(url, '_blank');
            }}
            className="border-[rgba(239,68,68,0.4)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.08)]"
            title="Open full-screen TV broadcast display"
          >
            📺 Broadcast
          </Button>
          {/* Public Scoreboard link */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${window.location.origin}/public/tournaments/${tournamentId}`;
              window.open(url, '_blank');
            }}
            className="border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.08)]"
            title="Open public live scoreboard"
          >
            🏆 Scoreboard
          </Button>
          <Button
            variant="outline"
            onClick={handleShare}
            size="sm"
            className="border-[#252a37] text-[#f5c518] hover:bg-[#161a26]"
            data-testid="tournament-share-button"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <Button variant="outline" onClick={fetchAll} size="sm" className="border-[#252a37] text-[#8a93a3] hover:text-[#f0f2f7]">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport} size="sm" className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {/* Auto-score + Recalculate — admin only */}
          {admin && (
            <>
            <Button variant="outline" size="sm"
              className="border-[rgba(245,197,24,0.4)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)]"
              onClick={async () => {
                if (!confirm('Auto-score all pending encounters using existing roster players?\n\nThis will fill in dummy scores for unscored matches.')) return;
                const token = localStorage.getItem('btccp_access_token');
                const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/auto-score?force=true`, {
                  method:'POST', headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
                });
                const d = await res.json();
                if (res.ok) { toast.success(d.message); fetchAll(); }
                else toast.error(d.detail || 'Failed');
              }}>
              ⚡ Auto-Score Matches
            </Button>
            {/* Recalculate points from existing match data */}
            <Button variant="outline" size="sm"
              className="border-[rgba(59,130,246,0.4)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.08)]"
              onClick={async () => {
                if (!confirm('Recalculate all points using individual match scoring (2 pts/win, 1 pt/draw)?\n\nThis updates totals from existing match results.')) return;
                const token = localStorage.getItem('btccp_access_token');
                const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/recalculate-points`, {
                  method:'POST', headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
                });
                const d = await res.json();
                if (res.ok) { toast.success(d.message); fetchAll(); }
                else toast.error(d.detail || 'Failed');
              }}>
              🔄 Recalculate Points
            </Button>
            {/* Generate Fixtures (Group Stage) */}
            <Button variant="outline" size="sm"
              className="border-[rgba(59,130,246,0.4)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.08)]"
              onClick={() => setFixtureModeOpen(true)}>
              ⚔️ Generate Fixtures
            </Button>
            {/* Generate sport-specific knockouts */}
            <Button variant="outline" size="sm"
              className="border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.08)]"
              onClick={async () => {
                if (!confirm('Generate sport-specific knockout draws?\n\nThis creates semi-finals and finals for each sport separately, seeded by per-sport group standings.\n\nExisting knockout encounters will be replaced.')) return;
                const token = localStorage.getItem('btccp_access_token');
                const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/generate-sport-knockouts`, {
                  method:'POST', headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
                });
                const d = await res.json();
                if (res.ok) { toast.success(`Sport knockouts generated! ${d.encounters_created} encounters created across ${d.sports?.length} sports.`); fetchAll(); }
                else toast.error(d.detail || 'Failed to generate knockouts');
              }}>
              🏆 Generate Sport Knockouts
            </Button>
            {/* Manage Tournament Admins */}
            <Button variant="outline" size="sm"
              className="border-[rgba(245,197,24,0.4)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)] relative"
              onClick={() => setAddAdminOpen(true)}>
              <Users className="h-3.5 w-3.5 mr-1.5"/>
              Admins {tournAdmins.length > 0 && <span className="ml-1 bg-[#f5c518] text-[#0b0f18] text-[9px] font-black px-1.5 py-0.5 rounded-full">{tournAdmins.length}</span>}
            </Button>
            {/* Assign existing teams to this tournament */}
            <Button variant="outline" size="sm"
              className="border-[rgba(59,130,246,0.4)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.08)]"
              onClick={openAssignTeams}
              data-testid="assign-teams-button">
              <Users className="h-3.5 w-3.5 mr-1.5"/>Assign Teams
            </Button>
            {/* Tournament Report */}
            <Button variant="outline" size="sm"
              className="border-[rgba(245,197,24,0.4)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)]"
              onClick={() => navigate(`/admin/tournaments/${tournamentId}/report`)}>
              📋 View Report
            </Button>
            </>
          )}
        </div>
      </div>

      {/* Tournament meta info */}
      {(tournament.start_date || tournament.venue_name) && (
        <div className="flex items-center gap-4 flex-wrap mb-2">
          {tournament.start_date && (
            <span className="text-sm text-[#8a93a3] flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(tournament.start_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
              {tournament.end_date && ` – ${new Date(tournament.end_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`}
            </span>
          )}
          {tournament.venue_name && (
            <span className="text-sm text-[#8a93a3] flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {tournament.venue_name}
            </span>
          )}
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {(() => {
          // Use encounters (new system) if available, else fall back to fixtures
          const totalMatches = encounters.length > 0 ? encounters.length : fixtures.length;
          const totalTies    = encounters.reduce((s, e) =>
            s + (e.sport_results||[]).reduce((ss, sr) => ss + (sr.matches||[]).length, 0), 0);
          const encCompleted = encounters.filter(e => e.status === 'completed').length;
          const encLive      = encounters.filter(e => e.status === 'in_progress').length;
          const completed    = encounters.length > 0 ? encCompleted : completedCount;
          const live         = encounters.length > 0 ? encLive : liveCount;
          const remaining    = totalMatches - completed;
          return [
            { label: totalTies > 0 ? `${totalMatches} Enc · ${totalTies} Ties` : 'Total Matches', value: totalMatches, icon: CalendarDays, tab: admin ? 'encounters' : null },
            { label: 'Completed',     value: completed,    icon: CheckCircle2, green: true, tab: admin ? 'encounters' : null },
            { label: 'Live Now',      value: live,         icon: Play, red: live > 0, tab: admin ? 'encounters' : null },
            { label: 'Remaining',     value: remaining,    icon: Clock, tab: admin ? 'encounters' : null },
          ];
        })().map(({ label, value, icon: Icon, green, red, tab }) => (
          <Card key={label}
            className={`bg-[#161a26] border-[#252a37] ${tab ? 'cursor-pointer hover:border-[rgba(245,197,24,0.4)] hover:-translate-y-0.5 transition-all' : ''}`}
            onClick={() => tab && setActiveTab(tab)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1d2230]">
                <Icon className={`h-4 w-4 ${green ? 'text-[#22c55e]' : red ? 'text-[#ef4444]' : 'text-[#f5c518]'}`} />
              </div>
              <div>
                <p className="text-xl font-bold text-[#f0f2f7]">{value}</p>
                <p className="text-xs text-[#8a93a3]">{label}</p>
              </div>
              {tab && <ChevronRight className="h-4 w-4 text-[#8a93a3] ml-auto"/>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs: Fixtures + Standings + Bracket */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#161a26] border border-[#252a37] mb-6">
          <TabsTrigger value="fixtures"  className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f0f2f7]">
            Fixtures ({fixtures.length})
          </TabsTrigger>
          <TabsTrigger value="standings" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f0f2f7]">
            Standings ({standings.length})
          </TabsTrigger>
          <TabsTrigger value="gstandings" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            Group Standings
          </TabsTrigger>
          {['single_elim', 'groups_plus_ko'].includes(tournament.format) && (
            <TabsTrigger value="bracket" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
              <GitBranch className="h-3.5 w-3.5 mr-1.5" />
              Bracket
            </TabsTrigger>
          )}
          {/* Admin-only tabs */}
          {admin && (
            <>
              <TabsTrigger value="registration" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Registration
              </TabsTrigger>
              <TabsTrigger value="sponsors" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Award className="h-3.5 w-3.5 mr-1.5" />
                Sponsors {tournSponsors.length > 0 && `(${tournSponsors.length})`}
              </TabsTrigger>
              <TabsTrigger value="format" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Settings className="h-3.5 w-3.5 mr-1.5" />
                Format
              </TabsTrigger>
              <TabsTrigger value="groups" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Groups
              </TabsTrigger>
              <TabsTrigger value="teams_mgmt" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Teams & Players
              </TabsTrigger>
              <TabsTrigger value="encounters" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f5c518]">
                <Trophy className="h-3.5 w-3.5 mr-1.5" />
                Matches
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* FIXTURES TAB */}
        <TabsContent value="fixtures">
          {Object.keys(byRound).length === 0 ? (
            <Card className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-12 text-center">
                <CalendarDays className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
                <p className="text-[#8a93a3]">No fixtures yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(byRound).map(([round, fxs]) => (
                <div key={round}>
                  <h3 className="text-sm font-semibold text-[#8a93a3] uppercase tracking-wider mb-3">
                    {formatRound(round)}
                  </h3>
                  <div className="space-y-2">
                    {fxs.map((fx) => {
                      const home = fx.home || fx.team1 || fx.home_team || {};
                      const away = fx.away || fx.team2 || fx.away_team || {};
                      const fixtureMatchId = fx.match_id || fx.id;
                      const isLive = fx.status?.toLowerCase() === 'live';
                      const isDone = ['completed','finished'].includes(fx.status?.toLowerCase());
                      const sched  = schedule[fx.id];
                      return (
                        <Card
                          key={fx.id}
                          className={`bg-[#161a26] border-[#252a37] transition-colors ${
                            isLive ? 'ring-1 ring-[rgba(239,68,68,0.3)]' : ''
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-20 flex-shrink-0">
                                <StatusBadge status={fx.status} />
                              </div>

                              {/* Home */}
                              <div className="flex-1 text-right">
                                <span className="font-medium text-[#f0f2f7] text-sm">{home.name || 'TBD'}</span>
                              </div>

                              {/* Score */}
                              <div className={`px-4 text-center font-bold tabular-nums text-lg ${
                                isLive ? 'text-[#f5c518]' : 'text-[#f0f2f7]'
                              }`}>
                                {isDone || isLive ? (
                                  `${fx.home_score ?? fx.score1 ?? fx.score?.home ?? '–'} : ${fx.away_score ?? fx.score2 ?? fx.score?.away ?? '–'}`
                                ) : (
                                  <span className="text-[#8a93a3] text-sm">vs</span>
                                )}
                              </div>

                              {/* Away */}
                              <div className="flex-1">
                                <span className="font-medium text-[#f0f2f7] text-sm">{away.name || 'TBD'}</span>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {(fx.status === 'live' || fx.status === 'scheduled') && fixtureMatchId && (
                                  <Link to={`/score/${fixtureMatchId}`}>
                                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#f5c518] hover:text-[#f7d24a] px-2">
                                      <Target className="h-3 w-3 mr-1" />
                                      Score
                                    </Button>
                                  </Link>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openScheduler(fx)}
                                  className={`h-7 px-2 ${sched?.time ? 'text-[#f5c518]' : 'text-[#8a93a3] hover:text-[#f0f2f7]'}`}
                                  title="Set match time"
                                  data-testid="fixture-schedule-button"
                                >
                                  <CalendarClock className="h-3.5 w-3.5" />
                                </Button>
                                {/* Delete fixture */}
                                {admin && !isDone && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={async () => {
                                      if (!confirm(`Delete this fixture (${home.name || 'Home'} vs ${away.name || 'Away'})?`)) return;
                                      try {
                                        const token = localStorage.getItem('btccp_access_token');
                                        const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters/${fx.id}`, {
                                          method: 'DELETE',
                                          headers: { Authorization: `Bearer ${token}` },
                                        });
                                        if (res.ok) {
                                          toast.success('Fixture deleted');
                                          await fetchAll(); // await so list refreshes immediately
                                        } else {
                                          const data = await res.json().catch(() => ({}));
                                          toast.error(`Delete failed: ${data.detail || res.status}`);
                                        }
                                      } catch (err) { toast.error('Failed to delete fixture'); }
                                    }}
                                    className="h-7 px-2 text-[#ef4444] hover:text-white hover:bg-[rgba(239,68,68,0.15)] border border-[rgba(239,68,68,0.3)]"
                                    title="Delete fixture"
                                    data-testid={`delete-fixture-${fx.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Schedule info */}
                            {(fx.scheduled_at || fx.venue || sched?.time) && (
                              <div className="mt-2 pt-2 border-t border-[#252a37]/50 flex items-center justify-between text-xs text-[#8a93a3]">
                                <div className="flex items-center gap-3">
                                  {sched?.time && (
                                    <span className="flex items-center gap-1 text-[#f5c518]">
                                      <Clock className="h-3 w-3" />
                                      {sched.date && `${new Date(sched.date + 'T00:00').toLocaleDateString([], {month:'short',day:'numeric'})} · `}{sched.time}
                                      {sched.court && <span className="text-[#8a93a3]"> · {sched.court}</span>}
                                    </span>
                                  )}
                                  {!sched?.time && fx.scheduled_at && <span>{new Date(fx.scheduled_at).toLocaleString()}</span>}
                                  {fx.venue && <span>📍 {fx.venue}</span>}
                                </div>
                                {sched?.time && (
                                  <button
                                    onClick={() => clearSchedule(fx.id)}
                                    className="text-[#8a93a3] hover:text-[#ef4444] text-[10px]"
                                  >
                                    clear
                                  </button>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* STANDINGS TAB */}
        <TabsContent value="standings">
          {standings.length > 0 && (
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm"
                onClick={() => window.open(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/export/standings.csv`, '_blank')}
                className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26] text-xs">
                <Download className="h-3.5 w-3.5 mr-1.5"/>Export Standings CSV
              </Button>
            </div>
          )}
          {standings.length === 0 ? (
            <Card className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-12 text-center">
                <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
                <p className="text-[#8a93a3]">Standings will appear once matches are played</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#161a26] border-[#252a37]">
              <ScrollArea className="w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#252a37] bg-[#1d2230]/60">
                      <TableHead className="text-xs uppercase text-[#8a93a3] w-10">#</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3]">Team</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3] text-center">MP</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3] text-center">W</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3] text-center">L</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3] text-center">D</TableHead>
                      <TableHead className="text-xs uppercase text-[#8a93a3] text-center">Pts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((row, idx) => (
                      <TableRow key={row.team_id || row.id || idx} className="border-b border-[#252a37] hover:bg-[#1d2230]/30">
                        <TableCell>
                          <span className={`text-sm font-bold ${idx < 2 ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>
                            {idx + 1}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-full bg-[#1d2230] flex items-center justify-center text-[#f5c518] font-bold text-xs">
                              {(row.team_name || row.name)?.[0] || 'T'}
                            </div>
                            <span className="font-medium text-[#f0f2f7] text-sm">{row.team_name || row.name || 'Team'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-[#8a93a3]">{row.played ?? row.mp ?? 0}</TableCell>
                        <TableCell className="text-center text-[#22c55e] font-medium">{row.wins ?? row.w ?? 0}</TableCell>
                        <TableCell className="text-center text-[#ef4444]">{row.losses ?? row.l ?? 0}</TableCell>
                        <TableCell className="text-center text-[#8a93a3]">{row.draws ?? row.d ?? 0}</TableCell>
                        <TableCell className="text-center font-bold text-[#f5c518]">{row.points ?? row.pts ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </Card>
          )}
        </TabsContent>

        {/* BRACKET TAB — knockout formats */}
        <TabsContent value="bracket">
          <BracketView fixtures={fixtures} schedule={schedule} />
        </TabsContent>

        {/* REGISTRATION TAB */}
        <TabsContent value="registration">
          <RegistrationTab
            categories={categories}
            orgTeams={orgTeams}
            registeredTeamIds={registeredTeamIds}
            fixtureCount={fixtures.length}
            onManageRegistration={openRegistration}
            onSetupTeams={() => { setSetupNumTeams('4'); setSetupTeamForms(Array.from({length:4},(_,i)=>emptyTeamSetup(i))); setTeamSetupOpen(true); }}
            orgSlug={orgSlug}
          />
        </TabsContent>

        {/* SPONSORS TAB */}
        <TabsContent value="sponsors">
          <div className="space-y-4" data-testid="sponsors-tab">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#f0f2f7]">Tournament Sponsors</h3>
                <p className="text-sm text-[#8a93a3]">Sponsors associated with this tournament</p>
              </div>
              {admin && (
              <Button
                onClick={() => { setAddSponsorOpen(true); setSponsorForm({ name: '', type_id: sponsorTypes[0]?.id || '', tagline: '', website_url: '' }); }}
                className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                data-testid="add-sponsor-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Sponsor
              </Button>
              )}
            </div>

            {tournSponsors.length === 0 ? (
              <Card className="bg-[#161a26] border-[#252a37]">
                <CardContent className="p-12 text-center">
                  <Award className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
                  <p className="text-[#f0f2f7] font-medium mb-1">No sponsors yet</p>
                  <p className="text-sm text-[#8a93a3]">Add sponsors to showcase them in this tournament</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tournSponsors.map((ts, idx) => {
                  const sp = ts.sponsor || ts;
                  const typeColor = ts.type_code === 'title' ? '#f5c518'
                    : sponsorTypes.find(t => t.code === ts.type_code)?.color || '#8a93a3';
                  // Find team info from orgTeams
                  const team = orgTeams.find(t => t.id === (sp.team_id || ts.team_id));
                  const isTitle = ts.type_code === 'title';
                  // For title sponsors (no team): use sponsor's own name/logo
                  const teamName  = team?.name || sp.team_name || ts.team_name
                                    || (isTitle ? (sp.name || ts.name) : null);
                  const teamLogo  = team?.logo_url || sp.team_logo_url || ts.team_logo_url
                                    || (isTitle ? (sp.logo_url || ts.logo_url) : null);
                  const teamColor = team?.color || sp.team_color || (isTitle ? '#f5c518' : '#3b82f6');

                  return (
                    <Card key={ts.id || idx} className="bg-[#161a26] border-[#252a37]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          {/* Team logo */}
                          <div className="h-12 w-12 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center font-bold text-white text-lg border-2"
                            style={{ backgroundColor: teamColor, borderColor: `${teamColor}60` }}>
                            {teamLogo
                              ? <img src={teamLogo} className="h-full w-full object-cover" alt={teamName} onError={e=>{e.currentTarget.style.display='none'}}/>
                              : (teamName?.[0] || '?')}
                          </div>
                          <div className="min-w-0 flex-1">
                            {/* Team name — primary */}
                            <p className="font-bold text-[#f0f2f7] truncate">{teamName || '—'}</p>
                            {/* Type badge */}
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${typeColor}20`, color: typeColor }}>
                              {ts.type_code === 'title' ? '★ Title Sponsor' : ts.type_code || 'Sponsor'}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Add Sponsor Dialog */}
            <Dialog open={addSponsorOpen} onOpenChange={setAddSponsorOpen}>
              <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md" data-testid="add-sponsor-dialog">
                <DialogHeader>
                  <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
                    <Award className="h-5 w-5 text-[#f5c518]" />
                    Add Sponsor to Tournament
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#8a93a3]">Sponsor Name *</Label>
                    <Input
                      value={sponsorForm.name}
                      onChange={e => setSponsorForm({ ...sponsorForm, name: e.target.value })}
                      placeholder="e.g., Acme Corp"
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                      data-testid="sponsor-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8a93a3]">Sponsor Type</Label>
                    <Select
                      value={sponsorForm.type_id}
                      onValueChange={v => setSponsorForm({ ...sponsorForm, type_id: v })}
                    >
                      <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#161a26] border-[#252a37]">
                        {sponsorTypes.map(t => (
                          <SelectItem key={t.id} value={t.id} className="text-[#f0f2f7] focus:bg-[#1d2230]">
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#8a93a3]">Tagline (optional)</Label>
                    <Input
                      value={sponsorForm.tagline}
                      onChange={e => setSponsorForm({ ...sponsorForm, tagline: e.target.value })}
                      placeholder="e.g., Official Kit Partner"
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8a93a3]">Website (optional)</Label>
                    <Input
                      value={sponsorForm.website_url}
                      onChange={e => setSponsorForm({ ...sponsorForm, website_url: e.target.value })}
                      placeholder="https://example.com"
                      className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddSponsorOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
                  <Button onClick={handleAddSponsor} disabled={addingSponsor} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]" data-testid="add-sponsor-submit-button">
                    {addingSponsor ? 'Adding...' : 'Add Sponsor'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* FORMAT TAB */}
        <TabsContent value="format">
          {/* Fix scoring rules button */}
          <div className="flex justify-end mb-3">
            <Button size="sm" variant="outline"
              className="border-[rgba(245,197,24,0.4)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)] text-xs"
              onClick={async () => {
                const token = localStorage.getItem('btccp_access_token');
                const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/fix-sport-formats`, {
                  method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success(`Scoring rules fixed for ${data.updated} sports (TT:11pts, Badminton:21pts, Pickleball:15pts, Chess/Carrom:result)`);
                  fetchAll();
                } else toast.error('Failed to fix formats');
              }}>
              Fix Scoring Rules
            </Button>
          </div>
          <SportFormatsTab
            tournamentId={tournamentId}
            orgSlug={orgSlug}
            availableSports={availableSports}
          />

          {/* Qualification Mode */}
          <div className="mt-4 bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
            <div className="px-4 py-3 bg-[#10131c] border-b border-[#252a37]">
              <p className="text-sm font-bold text-[#f0f2f7]">Qualification Mode</p>
              <p className="text-[10px] text-[#8a93a3] mt-0.5">How teams qualify for the knockouts</p>
            </div>
            <div className="p-4 flex gap-3">
              {[
                { mode:'per_group', label:'Top 2 per Group', desc:'Top 2 from each group (default)' },
                { mode:'overall',   label:'Top 2 Overall',   desc:'Best 2 teams across ALL groups combined' },
              ].map(opt => {
                const active = (tournament?.qualification_mode || 'per_group') === opt.mode;
                return (
                  <button key={opt.mode}
                    onClick={async () => {
                      const token = localStorage.getItem('btccp_access_token');
                      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}`, {
                        method:'PATCH', headers:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},
                        body: JSON.stringify({qualification_mode: opt.mode}),
                      });
                      if (res.ok) { toast.success(`Qualification: ${opt.label}`); fetchAll(); }
                    }}
                    className={`flex-1 px-4 py-3 rounded-xl border text-left transition-all ${
                      active
                        ? 'bg-[rgba(245,197,24,0.1)] border-[rgba(245,197,24,0.5)] text-[#f5c518]'
                        : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[rgba(245,197,24,0.3)]'
                    }`}>
                    <p className="text-sm font-bold">{opt.label}</p>
                    <p className="text-[10px] mt-0.5 opacity-70">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Player Categories for this tournament */}
          <div className="mt-6 bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
            <div className="px-4 py-3 bg-[#10131c] border-b border-[#252a37] flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-[#f0f2f7]">Player Categories for this Tournament</p>
                <p className="text-[10px] text-[#8a93a3] mt-0.5">Select which categories players can be assigned when joining this tournament's teams</p>
              </div>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2 mb-3">
                {allCategories.map(cat => {
                  const active = (tournament?.player_categories || []).includes(cat.code);
                  return (
                    <button key={cat.id}
                      onClick={async () => {
                        const current = tournament?.player_categories || [];
                        const next = active
                          ? current.filter(c => c !== cat.code)
                          : [...current, cat.code];
                        await saveTournamentCategories(next);
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                        active
                          ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
                          : 'bg-[#1d2230] text-[#8a93a3] border-[#252a37] hover:border-[rgba(245,197,24,0.4)] hover:text-[#f0f2f7]'
                      }`}>
                      {active && <Check className="h-3 w-3"/>}
                      {cat.name}
                    </button>
                  );
                })}
              </div>
              {allCategories.length === 0 && (
                <p className="text-xs text-[#8a93a3] italic">No categories defined. Create categories in the Players section first.</p>
              )}
              {(tournament?.player_categories || []).length > 0 && (
                <p className="text-[10px] text-[#22c55e]">
                  Active: {(tournament.player_categories || []).join(', ')}
                </p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* GROUPS TAB */}
        <TabsContent value="groups">
          <GroupsTab
            tournamentId={tournamentId}
            orgSlug={orgSlug}
            orgTeams={orgTeams}
            admin={admin}
          />
        </TabsContent>

        {/* TEAMS & PLAYERS TAB */}
        <TabsContent value="teams_mgmt">
          {(() => {
            const tournTeams = orgTeams.filter(t => t.tournament_id === tournamentId);
            return (
              <div className="space-y-4">
                {/* Header actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline"
                    className="border-[rgba(59,130,246,0.4)] text-[#3b82f6]"
                    onClick={() => { openGroupAssign(); }}>
                    Group Assignment
                  </Button>
                  <Button size="sm" variant="ghost"
                    className="text-[#8a93a3] hover:text-[#f0f2f7]"
                    onClick={() => {
                      const tournTeams = orgTeams.filter(t => t.tournament_id === tournamentId);
                      fetchTeamRosters(tournTeams);
                    }}>
                    ↻ Refresh
                  </Button>
                  <p className="text-xs text-[#8a93a3] ml-auto">{tournTeams.length} teams in this tournament</p>
                </div>

                {tournTeams.length === 0 && (
                  <div className="text-center py-12 text-[#8a93a3]">
                    <p className="text-sm">No teams assigned to this tournament yet.</p>
                    <p className="text-xs mt-1">Use the <strong>Assign Teams</strong> button in the header.</p>
                  </div>
                )}

                {tournTeams.map(team => {
                  const roster = teamRosters[team.id] || [];
                  return (
                    <div key={team.id} className="bg-[#161a26] border border-[#252a37] rounded-2xl overflow-hidden">
                      {/* Team header */}
                      <div className="flex items-center gap-3 px-4 py-3 bg-[#10131c] border-b border-[#252a37]">
                        <div className="h-8 w-8 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-sm text-white overflow-hidden"
                          style={{ backgroundColor: team.color||'#3b82f6' }}>
                          {team.logo_url?.startsWith('data:')
                            ? <img src={team.logo_url} className="h-full w-full object-cover" alt=""/>
                            : team.short_name?.[0] || team.name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#f0f2f7] truncate">{team.name}</p>
                          <p className="text-[10px] text-[#8a93a3]">{roster.length} player{roster.length!==1?'s':''}</p>
                        </div>
                        <Button size="sm"
                          className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] h-7 text-xs"
                          onClick={() => {
                            setAddPlayerTarget({ teamId: team.id, teamName: team.name });
                            setAddPlayerForm({ player_id:'', category:'' });
                            fetchPlayerCategories();
                            // Show only tournament's active categories (or all if none configured)
                            const activeCodes = tournament?.player_categories || [];
                            setTimeout(() => {
                              setPlayerCategories(prev => {
                                const cats = allCategories.length > 0 ? allCategories : prev;
                                return activeCodes.length > 0
                                  ? cats.filter(c => activeCodes.includes(c.code))
                                  : cats;
                              });
                            }, 300);
                            if (!teamRosters[team.id]) fetchTeamRosters([team]);
                          }}>
                          + Add Player
                        </Button>
                      </div>
                      {/* Player list */}
                      {roster.length > 0 ? (
                        <div className="divide-y divide-[#252a37]/50">
                          {roster.map(r => {
                            const isEditing = editingRoster?.teamId === team.id && editingRoster?.playerId === r.player_id;
                            // Build category options: use tournament's active cats or all org cats
                            const activeCodes = tournament?.player_categories || [];
                            const catOptions = (activeCodes.length > 0
                              ? allCategories.filter(c => activeCodes.includes(c.code))
                              : allCategories);
                            return (
                            <div key={r.id} className="text-xs group">
                              {/* Main row — click to edit category */}
                              <div
                                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#1d2230] transition-colors"
                                onClick={() => {
                                  if (isEditing) { setEditingRoster(null); return; }
                                  setEditingRoster({ teamId: team.id, playerId: r.player_id });
                                  setEditCat(r.category || '');
                                }}>
                                <p className="flex-1 text-[#f0f2f7] font-medium truncate">{r.player_name || r.player_id}</p>
                                {r.category ? (
                                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#1d2230] text-[#f5c518] border border-[#252a37] capitalize">{r.category}</span>
                                ) : (
                                  <span className="text-[9px] px-2 py-0.5 rounded-full border border-dashed border-[#252a37] text-[#8a93a3] opacity-0 group-hover:opacity-100 transition-opacity">Set category</span>
                                )}
                                {r.is_platinum && (
                                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(245,197,24,0.12)] text-[#f5c518] border border-[rgba(245,197,24,0.3)]">★ Platinum</span>
                                )}
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(`Remove ${r.player_name || 'player'} from ${team.name}?`)) return;
                                    const token = localStorage.getItem('btccp_access_token');
                                    const res = await fetch(`/api/orgs/${orgSlug}/teams/${team.id}/roster/${r.player_id}`, {
                                      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
                                    });
                                    if (res.ok) {
                                      toast.success(`${r.player_name || 'Player'} removed`);
                                      fetchTeamRosters([team]);
                                    } else { toast.error('Failed to remove player'); }
                                  }}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-[#ef4444] hover:text-red-400 flex-shrink-0 p-0.5 rounded"
                                  title="Remove from team">
                                  <X className="h-3.5 w-3.5"/>
                                </button>
                              </div>
                              {/* Inline category editor */}
                              {isEditing && (
                                <div className="flex items-center gap-2 px-4 pb-2.5 bg-[#1d2230]">
                                  <select
                                    value={editCat}
                                    onChange={e => setEditCat(e.target.value)}
                                    className="flex-1 bg-[#0b0f18] border border-[#252a37] text-[#f0f2f7] text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-[#f5c518]">
                                    <option value="">— No category —</option>
                                    {catOptions.map(c => (
                                      <option key={c.id||c.code} value={c.code}>{c.name}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={saveRosterCategory}
                                    disabled={savingRosterCat}
                                    className="px-3 py-1 bg-[#22c55e] text-white text-xs rounded-lg hover:bg-[#16a34a] disabled:opacity-50 flex-shrink-0">
                                    {savingRosterCat ? '…' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => setEditingRoster(null)}
                                    className="px-2 py-1 text-[#8a93a3] hover:text-[#f0f2f7] text-xs flex-shrink-0">
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-[#8a93a3] px-4 py-3 italic">No players assigned yet</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>

        {/* ENCOUNTERS TAB */}
        <TabsContent value="encounters">
          {/* Create Draw + Add Knockouts + Assign Seeds — admins only */}
          {admin && (
          <div className="flex flex-col gap-3 mb-4 p-3 bg-[#161a26] border border-[#252a37] rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#f0f2f7]">Tournament Draw</p>
                <p className="text-xs text-[#8a93a3]">Generate fixtures for the knockout stage</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* Direct Final + 3rd Place (no semis) */}
                <Button onClick={async () => {
                  if (!confirm('Generate Final & 3rd Place?\n\nThis seeds the Final (1st vs 2nd) and 3rd Place match (3rd vs 4th) based on current overall league standings. No semi-finals.\n\nExisting knockout encounters will be replaced.')) return;
                  const token = localStorage.getItem('btccp_access_token');
                  const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/generate-finals`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  });
                  const data = await res.json();
                  if (res.ok) { toast.success(`Finals generated! ${data.encounters_created} encounter(s) created.`); fetchAll(); }
                  else { toast.error(data.detail || 'Failed to generate finals'); }
                }} size="sm"
                  className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] font-semibold"
                  data-testid="generate-finals-button">
                  Generate Final & 3rd Place
                </Button>
                {/* SF → Final (classic format) */}
                <Button onClick={async () => {
                  if (!confirm('Generate sport-specific knockout draws?\n\nThis creates separate SF1, SF2 and Final for each sport, seeded by per-sport group standings.\n\nExisting knockout encounters will be replaced.')) return;
                  const token = localStorage.getItem('btccp_access_token');
                  const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/generate-sport-knockouts`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  });
                  const data = await res.json();
                  if (res.ok) { toast.success(`Sport knockouts generated! ${data.encounters_created} encounters across ${data.sports?.length} sports.`); fetchAll(); }
                  else { toast.error(data.detail || 'Failed'); }
                }} size="sm" variant="outline"
                  className="border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.08)]"
                  data-testid="add-knockouts-button">
                  Semi-Finals → Final (Classic)
                </Button>
                <Button onClick={() => setDrawOpen(true)} size="sm" variant="outline"
                  className="border-[#252a37] text-[#8a93a3] hover:bg-[#1d2230]"
                  data-testid="create-draw-button">
                  Create / Reset Draw
                </Button>
              </div>
            </div>

            {/* Seed → Team assignment */}
            <SeedAssignPanel
              tournamentId={tournamentId}
              orgSlug={orgSlug}
              orgTeams={orgTeams}
              onAssigned={fetchAll}
            />
          </div>
          )}
          <EncountersTab
            tournamentId={tournamentId}
            orgSlug={orgSlug}
            admin={admin}
          />
        </TabsContent>

        {/* GROUP STANDINGS TAB */}
        <TabsContent value="gstandings">
          <GroupStandingsTab
            tournamentId={tournamentId}
            orgSlug={orgSlug}
          />
        </TabsContent>
      </Tabs>
      <Dialog open={teamSetupOpen} onOpenChange={setTeamSetupOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="team-setup-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Users className="h-5 w-5 text-[#f5c518]" />
              Set Up Teams for {tournament?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="text-[#8a93a3] w-32 flex-shrink-0">Number of Teams</Label>
              <Select value={setupNumTeams} onValueChange={v => {
                setSetupNumTeams(v);
                const n = parseInt(v) || 4;
                setSetupTeamForms(Array.from({length:n},(_,i)=>emptyTeamSetup(i)));
              }}>
                <SelectTrigger className="flex-1 bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161a26] border-[#252a37]">
                  {[2,4,6,8,10,12,16].map(n=>(
                    <SelectItem key={n} value={String(n)} className="text-[#f0f2f7] focus:bg-[#1d2230]">{n} Teams</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Teams — Name, Code, Color, Logo, Sponsor</Label>
              <ScrollArea className="max-h-[320px] pr-1">
                <div className="space-y-2">
                  {setupTeamForms.map((tf, i) => (
                    <TeamFormRowEdit key={`team-form-${i}`} idx={i} data={tf}
                      onChange={(idx, updated) => setSetupTeamForms(prev => prev.map((t,j)=>j===idx?updated:t))}
                    />
                  ))}
                </div>
              </ScrollArea>
            </div>
            <p className="text-xs text-[#8a93a3]">
              Click the colored square to upload a logo. Teams will be created and fixtures generated automatically.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamSetupOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button
              onClick={handleTeamSetupCreate}
              disabled={settingUpTeams || setupTeamForms.filter(t=>t.name.trim()).length < 2}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="team-setup-create-button"
            >
              {settingUpTeams ? 'Creating...' : `Create ${setupTeamForms.filter(t=>t.name.trim()).length} Teams & Generate Fixtures`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registration / Generate Fixtures Dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="registration-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#f5c518]" />
              Manage Registration &amp; Generate Fixtures
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Fixture overwrite warning */}
            {fixtures.length > 0 && (
              <Alert className="bg-[rgba(239,68,68,0.08)] border-[rgba(239,68,68,0.3)]">
                <AlertTriangle className="h-4 w-4 text-[#ef4444]" />
                <AlertDescription className="text-[#f0f2f7] text-sm">
                  This tournament already has <strong>{fixtures.length} fixtures</strong>.
                  Generating new ones will replace them.
                </AlertDescription>
              </Alert>
            )}

            {/* Category selector */}
            {categories.length > 1 && (
              <div>
                <Label className="text-[#8a93a3] mb-1 block">Category</Label>
                <Select value={selectedCatId} onValueChange={setSelectedCatId}>
                  <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#161a26] border-[#252a37]">
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-[#f0f2f7] focus:bg-[#1d2230]">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Team list with seed inputs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#8a93a3]">
                  Teams — {Object.values(regTeams).filter(t => t.selected).length} selected
                </Label>
                <button
                  type="button"
                  onClick={() => {
                    const allSelected = orgTeams.every(t => regTeams[t.id]?.selected);
                    const next = {};
                    orgTeams.forEach(t => { next[t.id] = { ...regTeams[t.id], selected: !allSelected }; });
                    setRegTeams(next);
                  }}
                  className="text-xs text-[#f5c518] hover:text-[#f7d24a]"
                >
                  {orgTeams.every(t => regTeams[t.id]?.selected) ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <ScrollArea className="h-[220px] bg-[#1d2230] rounded-lg border border-[#252a37]">
                {orgTeams.length === 0
                  ? <p className="p-4 text-[#8a93a3] text-sm">No teams found. Add teams first.</p>
                  : orgTeams.map(team => {
                    const isSelected = regTeams[team.id]?.selected;
                    return (
                      <div
                        key={team.id}
                        className={`flex items-center gap-3 px-4 py-2.5 border-b border-[#252a37]/50 last:border-0 transition-colors ${
                          isSelected ? 'bg-[rgba(245,197,24,0.05)]' : ''
                        }`}
                      >
                        <div
                          onClick={() => toggleRegTeam(team.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                            isSelected ? 'bg-[#f5c518] border-[#f5c518]' : 'border-[#8a93a3] hover:border-[#f5c518]'
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3 text-[#0b0f18]" />}
                        </div>
                        <span
                          onClick={() => toggleRegTeam(team.id)}
                          className={`flex-1 text-sm font-medium cursor-pointer ${isSelected ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}
                        >
                          {team.name}
                          {team.short_name && <span className="ml-2 text-xs text-[#8a93a3]/60">{team.short_name}</span>}
                        </span>
                        {isSelected && (
                          <Input
                            type="number"
                            placeholder="Seed"
                            min="1"
                            value={regTeams[team.id]?.seed || ''}
                            onChange={e => setRegTeamSeed(team.id, e.target.value)}
                            className="w-16 h-7 bg-[#252a37] border-[#252a37] text-[#f0f2f7] text-xs text-center px-1"
                            onClick={e => e.stopPropagation()}
                          />
                        )}
                      </div>
                    );
                  })
                }
              </ScrollArea>
              <p className="text-xs text-[#8a93a3] mt-1">Click team to select/deselect. Seed is optional (blank = auto-assign).</p>
            </div>

            {/* Seeding method */}
            <div>
              <Label className="text-[#8a93a3] mb-1 block">Seeding Method</Label>
              <Select value={regSeeding} onValueChange={setRegSeeding}>
                <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#161a26] border-[#252a37]">
                  <SelectItem value="linear" className="text-[#f0f2f7] focus:bg-[#1d2230]">Linear (1 vs 2, 3 vs 4…)</SelectItem>
                  <SelectItem value="snake"  className="text-[#f0f2f7] focus:bg-[#1d2230]">Snake (1 vs last, 2 vs 2nd-last…)</SelectItem>
                  <SelectItem value="random" className="text-[#f0f2f7] focus:bg-[#1d2230]">Random Draw</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button
              onClick={handleGenerateFixtures}
              disabled={generating || Object.values(regTeams).filter(t => t.selected).length < 2}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="registration-generate-button"
            >
              {generating
                ? 'Generating…'
                : (() => {
                    const n = Object.values(regTeams).filter(t => t.selected).length;
                    return `Generate Fixtures (${n} ${n === 1 ? 'team' : 'teams'})`;
                  })()
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tournament Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37]" data-testid="tournament-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#f5c518]" />
              Edit Tournament
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3]">Name</Label>
              <Input
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="tournament-edit-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8a93a3]">Start Date</Label>
                <Input
                  type="date"
                  value={editForm.start_date}
                  onChange={e => setEditForm({ ...editForm, start_date: e.target.value })}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                  data-testid="tournament-edit-start-date"
                />
              </div>
              <div>
                <Label className="text-[#8a93a3]">End Date</Label>
                <Input
                  type="date"
                  value={editForm.end_date}
                  onChange={e => setEditForm({ ...editForm, end_date: e.target.value })}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                  data-testid="tournament-edit-end-date"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Venue</Label>
              <Input
                value={editForm.venue_name}
                onChange={e => setEditForm({ ...editForm, venue_name: e.target.value })}
                placeholder="e.g., BTCCP Sports Arena"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="tournament-edit-venue-input"
              />
            </div>

            {/* Banner upload */}
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Tournament Banner</Label>
              <div
                onClick={() => bannerFileRef.current?.click()}
                className={`relative w-full h-28 rounded-xl overflow-hidden border-2 border-dashed cursor-pointer transition-colors
                  ${bannerPreview ? 'border-[rgba(245,197,24,0.4)]' : 'border-[#252a37] hover:border-[rgba(245,197,24,0.3)]'}`}
                data-testid="tournament-banner-upload-area"
              >
                {bannerPreview ? (
                  <>
                    <img src={bannerPreview} className="w-full h-full object-cover" alt="banner preview"/>
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-xs flex items-center gap-1.5"><Upload className="h-3.5 w-3.5"/>Change banner</span>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-2">
                    <Upload className="h-6 w-6 text-[#8a93a3]"/>
                    <p className="text-xs text-[#8a93a3]">Click to upload banner image</p>
                    <p className="text-[10px] text-[#8a93a3]/60">JPG, PNG, WEBP · Max 3MB · Recommended: 1200×400px</p>
                  </div>
                )}
              </div>
              <input type="file" accept="image/*" ref={bannerFileRef} hidden onChange={handleBannerChange}
                data-testid="tournament-banner-file-input"/>
              {bannerPreview && (
                <button type="button" onClick={() => { setBannerPreview(null); setBannerB64(null); setEditForm({...editForm, banner_url: ''}); }}
                  className="mt-1.5 text-xs text-[#ef4444] hover:text-[#f87171]">
                  Remove banner
                </button>
              )}
            </div>

            <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#f5c518] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#8a93a3]">
                Individual fixture times are assigned automatically based on tournament dates.
                To schedule specific match times, use the mobile admin app.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button
              onClick={handleSaveTournament}
              disabled={saving}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="tournament-edit-save-button"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixture Schedule Dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-[#f5c518]" />
              Schedule Match
            </DialogTitle>
          </DialogHeader>
          {schedTarget && (
            <div className="space-y-4">
              {/* Match preview */}
              <div className="bg-[#1d2230] rounded-lg p-3 text-center text-sm">
                <span className="text-[#f0f2f7] font-medium">
                  {(schedTarget.home || schedTarget.team1)?.name || 'TBD'}
                </span>
                <span className="text-[#8a93a3] mx-2">vs</span>
                <span className="text-[#f0f2f7] font-medium">
                  {(schedTarget.away || schedTarget.team2)?.name || 'TBD'}
                </span>
              </div>

              <div>
                <Label className="text-[#8a93a3] mb-1 block">Date</Label>
                <DatePickerField
                  value={schedForm.date}
                  onChange={d => setSchedForm({ ...schedForm, date: d })}
                  placeholder="Pick match date"
                  testId="schedule-date-input"
                />
              </div>

              <div>
                <Label className="text-[#8a93a3] mb-1 block">Time</Label>
                <TimePickerField
                  value={schedForm.time}
                  onChange={t => setSchedForm({ ...schedForm, time: t })}
                  testId="schedule-time-input"
                />
              </div>

              <div>
                <Label className="text-[#8a93a3]">Court / Venue (optional)</Label>
                <Input
                  value={schedForm.court}
                  onChange={e => setSchedForm({ ...schedForm, court: e.target.value })}
                  placeholder="e.g., Court 1, Table 3"
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                  data-testid="schedule-court-input"
                />
              </div>

              <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
                <CalendarClock className="h-4 w-4 text-[#8a93a3] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#8a93a3]">
                  Schedule is saved <strong className="text-[#f0f2f7]">locally on this device</strong> and shown in fixtures, bracket, and the public scoreboard link for this browser.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedOpen(false)} className="border-[#252a37] text-[#f0f2f7]">
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchedule}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="schedule-save-button"
            >
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Draw Dialog — self-contained */}
      <TournamentDrawDialog
        open={drawOpen}
        onClose={() => setDrawOpen(false)}
        orgSlug={orgSlug}
        tournamentId={tournamentId}
        orgTeams={orgTeams}
        registeredTeamIds={registeredTeamIds}
        onDrawCreated={fetchAll}
      />

      {/* ── Fixture Mode Selection Dialog ── */}
      <Dialog open={fixtureModeOpen} onOpenChange={setFixtureModeOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              ⚔️ Generate Group Stage Fixtures
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-[#8a93a3]">Choose how matches are scheduled between groups:</p>
            {[
              { mode:'within_group', label:'Within Group',  icon:'🔄',
                desc:'Teams play ONLY teams in their own group (standard Round Robin). A1 vs A2, B1 vs B2.' },
              { mode:'cross_group',  label:'Cross Group',   icon:'⚡',
                desc:'Teams play teams from the OTHER group only (CBL format). A1 vs B1, A1 vs B2, A2 vs B1, A2 vs B2.' },
              { mode:'all_vs_all',   label:'All vs All',    icon:'🌐',
                desc:'Every team plays every other team regardless of group (full round robin).' },
            ].map(opt => (
              <button key={opt.mode}
                onClick={() => setFixtureMode(opt.mode)}
                className={`w-full px-4 py-3 rounded-xl border text-left transition-all ${
                  fixtureMode === opt.mode
                    ? 'bg-[rgba(59,130,246,0.12)] border-[rgba(59,130,246,0.5)] text-[#3b82f6]'
                    : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[rgba(59,130,246,0.3)]'
                }`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-base">{opt.icon}</span>
                  <p className="text-sm font-bold">{opt.label}</p>
                  {fixtureMode === opt.mode && <Check className="h-4 w-4 ml-auto text-[#3b82f6]"/>}
                </div>
                <p className="text-[11px] opacity-70 ml-6">{opt.desc}</p>
              </button>
            ))}
            <p className="text-[10px] text-[#ef4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-lg px-3 py-2">
              ⚠ Existing group stage encounters will be replaced. Knockout encounters are kept.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixtureModeOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button
              onClick={async () => {
                setGeneratingFixtures(true);
                try {
                  const token = localStorage.getItem('btccp_access_token');
                  const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/generate-encounters`, {
                    method:'POST',
                    headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'},
                    body: JSON.stringify({ fixture_mode: fixtureMode }),
                  });
                  const d = await res.json();
                  if (res.ok) {
                    const modeLabel = fixtureMode==='cross_group'?'Cross Group':fixtureMode==='all_vs_all'?'All vs All':'Within Group';
                    toast.success(`${modeLabel}: ${d.created} fixture${d.created!==1?'s':''} generated!`);
                    setFixtureModeOpen(false);
                    fetchAll();
                  } else { toast.error(d.detail || 'Failed to generate fixtures'); }
                } finally { setGeneratingFixtures(false); }
              }}
              disabled={generatingFixtures}
              className="bg-[#3b82f6] text-white hover:bg-[#2563eb]">
              {generatingFixtures ? 'Generating…' : 'Generate Fixtures'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Tournament Admins Dialog ── */}
      <Dialog open={addAdminOpen} onOpenChange={setAddAdminOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Users className="h-4 w-4 text-[#f5c518]"/>
              Add Tournament Admin
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-xs text-[#8a93a3]">
              Enter their phone number. They must already have an account. They will get full admin access to <strong className="text-[#f0f2f7]">only this tournament</strong>.
            </p>
            <div>
              <Label className="text-[#8a93a3] text-xs mb-1 block">Phone Number</Label>
              <Input
                value={addAdminPhone}
                onChange={e => setAddAdminPhone(e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"
                onKeyDown={e => e.key === 'Enter' && handleAddAdmin()}
              />
            </div>
            {/* Current admins */}
            {tournAdmins.length > 0 && (
              <div>
                <p className="text-[10px] text-[#8a93a3] font-bold uppercase tracking-widest mb-2">Current Tournament Admins</p>
                <div className="space-y-1.5">
                  {tournAdmins.map(a => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-[#1d2230] rounded-xl border border-[#252a37]">
                      <div className="h-7 w-7 rounded-full bg-[#f5c518] flex items-center justify-center font-black text-xs text-[#0b0f18] flex-shrink-0">
                        {(a.name||'?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[#f0f2f7] truncate">{a.name}</p>
                        <p className="text-[10px] text-[#8a93a3] truncate">{a.phone}</p>
                      </div>
                      <button onClick={() => handleRemoveAdmin(a.user_id, a.name)}
                        className="text-[#ef4444] hover:text-red-400 transition-colors">
                        <X className="h-4 w-4"/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAdminOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Close</Button>
            <Button onClick={handleAddAdmin} disabled={addingAdmin || !addAdminPhone.trim()}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              {addingAdmin ? 'Adding…' : 'Add Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Player to Team Dialog ── */}
      <Dialog open={!!addPlayerTarget} onOpenChange={v => !v && setAddPlayerTarget(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">
              Add Player to {addPlayerTarget?.teamName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Player selector */}
            <div>
              <Label className="text-[#8a93a3] text-xs mb-1 block">Select Player</Label>
              <select value={addPlayerForm.player_id}
                onChange={e => setAddPlayerForm(f => ({...f, player_id: e.target.value}))}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-3 py-2">
                <option value="">— choose a player —</option>
                {tournamentPlayers.map(p => (
                  <option key={p.id} value={p.id}>{p.full_name || p.display_name}</option>
                ))}
              </select>
            </div>
            {/* Category selector */}
            <div>
              <Label className="text-[#8a93a3] text-xs mb-1 block">Player Category</Label>
              <select value={addPlayerForm.category}
                onChange={e => setAddPlayerForm(f => ({...f, category: e.target.value}))}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-3 py-2">
                <option value="">— select category —</option>
                {playerCategories.map(c => (
                  <option key={c.id||c.code||c.name} value={c.code||c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPlayerTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleAddPlayer} disabled={savingPlayer || !addPlayerForm.player_id}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              {savingPlayer ? 'Adding…' : 'Add Player'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assign Teams to Groups Dialog ── */}
      <Dialog open={groupAssignOpen} onOpenChange={setGroupAssignOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Assign Teams to Groups</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {groupsData.length === 0 && (
              <p className="text-sm text-[#8a93a3]">No groups found. Create groups first.</p>
            )}
            {groupsData.map(grp => {
              const tournTeams = orgTeams.filter(t => t.tournament_id === tournamentId);
              return (
                <div key={grp.id} className="bg-[#1d2230] rounded-xl p-4 border border-[#252a37]">
                  <p className="text-sm font-bold text-[#f5c518] mb-3">Group {grp.letter}</p>
                  <div className="space-y-2">
                    {[0, 1, 2, 3].map(slot => {
                      const current = (groupSlots[grp.id]||[])[slot] || '';
                      const usedInThisGroup = (groupSlots[grp.id]||[]).filter((_, i) => i !== slot);
                      const usedInOtherGroups = groupsData
                        .filter(g => g.id !== grp.id)
                        .flatMap(g => groupSlots[g.id]||[]);
                      return (
                        <div key={slot} className="flex items-center gap-2">
                          <span className="text-[10px] text-[#8a93a3] w-12 flex-shrink-0">Slot {slot+1}</span>
                          <select value={current}
                            onChange={e => {
                              const v = e.target.value;
                              setGroupSlots(prev => {
                                const arr = [...(prev[grp.id]||[])];
                                while (arr.length <= slot) arr.push('');
                                arr[slot] = v;
                                return { ...prev, [grp.id]: arr };
                              });
                            }}
                            className="flex-1 bg-[#0b0f18] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 py-1.5">
                            <option value="">— empty —</option>
                            {tournTeams.map(t => {
                              const takenElsewhere = usedInOtherGroups.includes(t.id) || usedInThisGroup.includes(t.id);
                              return (
                                <option key={t.id} value={t.id} disabled={takenElsewhere && t.id !== current}>
                                  {t.name}{takenElsewhere && t.id !== current ? ' (assigned)' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupAssignOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={saveGroupAssign} disabled={savingGroupAssign || groupsData.length === 0}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              {savingGroupAssign ? 'Saving…' : 'Save Group Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assign Teams to Tournament Dialog ── */}
      <Dialog open={assignTeamsOpen} onOpenChange={setAssignTeamsOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Users className="h-4 w-4 text-[#3b82f6]"/>
              Assign Teams to Tournament
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-[#8a93a3] mb-3">
              Select which teams belong to this tournament. Teams already assigned are pre-checked.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {orgTeams.map(t => {
                const checked = assignSelected.has(t.id);
                const isCurrent = t.tournament_id === tournamentId;
                return (
                  <label key={t.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                      checked ? 'border-[rgba(59,130,246,0.5)] bg-[rgba(59,130,246,0.08)]' : 'border-[#252a37] hover:border-[rgba(59,130,246,0.3)]'
                    }`}>
                    <input type="checkbox" className="sr-only"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(assignSelected);
                        if (checked) next.delete(t.id); else next.add(t.id);
                        setAssignSelected(next);
                      }}/>
                    <div className="h-6 w-6 rounded-md flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                      style={{ backgroundColor: t.color || '#3b82f6' }}>
                      {t.logo_url?.startsWith('data:') ? <img src={t.logo_url} className="h-full w-full object-cover" alt=""/> : t.short_name?.[0] || t.name?.[0]}
                    </div>
                    <span className="flex-1 text-sm font-semibold text-[#f0f2f7] truncate">{t.name}</span>
                    {isCurrent && <span className="text-[9px] text-[#22c55e] font-bold">CURRENT</span>}
                    <div className={`h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      checked ? 'bg-[#3b82f6] border-[#3b82f6]' : 'border-[#252a37]'
                    }`}>
                      {checked && <Check className="h-2.5 w-2.5 text-white"/>}
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-[#8a93a3] mt-2">
              {assignSelected.size} team{assignSelected.size !== 1 ? 's' : ''} selected
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTeamsOpen(false)}
              className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleAssignTeams} disabled={assigning || assignSelected.size === 0}
              className="bg-[#3b82f6] text-white hover:bg-[#2563eb]"
              data-testid="confirm-assign-teams">
              {assigning ? 'Assigning…' : `Assign ${assignSelected.size} Teams`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
